import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import { fetchClientZipMap } from '@src/api';
import { formatDateToYMD } from '@src/utils/dateUtils';

const MAP_COLORS = ['#1d4ed8', '#c026d3', '#059669', '#d97706', '#0d9488', '#9333ea', '#f43f5e', '#5c6bc0'];
const DEFAULT_CENTER = [37.8, -96];
const MAP_HEIGHT = 640;
const FULLSCREEN_MAP_HEIGHT = '80vh';
const LEAD_MARKER_STYLE = {
  minRadius: 1,
  maxRadius: 12,
  scale: 2.2,
  zoomMultiplier: 1.18,
  baselineZoom: 6
};
const NORTH_AMERICA_BOUNDS = [
  [5, -170],
  [84, -52]
];
const OUT_OF_AREA_COLOR = '#c62828';

// KPI catalog backing the "Color by" selector. `format` drives the tooltip
// renderer; `key` matches the field name on the backend `kpis` dict.
const KPI_OPTIONS = [
  { key: 'total_leads', label: 'Leads', format: 'count' },
  { key: 'accepted_count', label: 'Accepted Leads', format: 'count' },
  { key: 'out_of_areas_count', label: 'Out-of-Area Leads', format: 'count' },
  { key: 'net_sales', label: 'Net Sales', format: 'count' },
  { key: 'gross_sales', label: 'Gross Sales', format: 'count' },
  { key: 'booked', label: 'Booked', format: 'count' },
  { key: 'demos', label: 'Demos', format: 'count' },
  { key: 'self_book_count', label: 'Self-Booked', format: 'count' },
  { key: 'total_spend', label: 'Total Spend', format: 'currency' },
  { key: 'net_amount', label: 'Net $', format: 'currency' },
  { key: 'gross_amount', label: 'Gross $', format: 'currency' },
  { key: 'cost_per_lead', label: 'Cost per Lead', format: 'currency' },
  { key: 'conversion_rate', label: 'Conversion %', format: 'percent' },
  { key: 'accepted_rate', label: 'Accepted %', format: 'percent' },
  { key: 'out_of_area_rate', label: 'Out-of-Area %', format: 'percent' },
];

const KPI_OPTION_MAP = Object.fromEntries(
  KPI_OPTIONS.map((option) => [option.key, option])
);

const DEFAULT_KPI = 'total_leads';
const KPI_STORAGE_KEY = 'client_zip_map_kpi';
const KPI_FILTER_STORAGE_KEY = 'client_zip_map_kpi_only_with_data';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const currencyDetailFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});
const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});
const countFormatter = new Intl.NumberFormat('en-US');

const formatKpiValue = (value, format) => {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  if (format === 'currency') {
    return num < 1000 ? currencyDetailFormatter.format(num) : currencyFormatter.format(num);
  }
  if (format === 'percent') {
    return percentFormatter.format(num);
  }
  return countFormatter.format(num);
};

const getTerritoryKey = (territoryId) => (territoryId === null || territoryId === undefined ? '__none__' : String(territoryId));

const withAlpha = (hex, alpha) => {
  if (!hex) return `rgba(84, 110, 122, ${alpha})`;
  let sanitized = hex.replace('#', '');
  if (sanitized.length === 3) {
    sanitized = sanitized
      .split('')
      .map((ch) => ch + ch)
      .join('');
  }
  const int = parseInt(sanitized, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isPolygonGeometry = (geometry) => {
  if (!geometry || !geometry.type) return false;
  return geometry.type === 'Polygon' || geometry.type === 'MultiPolygon';
};

// Center the map exactly once, the first time real data resolves a center.
// After that, leave the map alone — pan/zoom belongs to the user, and
// re-centering on every data refresh (date filter change, refetch, etc)
// yanks them back to the middle of the country mid-exploration.
const MapAutoCenter = ({ center, isDefault }) => {
  const map = useMap();
  const hasCenteredRef = useRef(false);
  useEffect(() => {
    if (hasCenteredRef.current) return;
    if (!center || isDefault) return;
    map.setView(center);
    hasCenteredRef.current = true;
  }, [center, isDefault, map]);
  return null;
};

MapAutoCenter.propTypes = {
  center: PropTypes.arrayOf(PropTypes.number).isRequired,
  isDefault: PropTypes.bool,
};

const MapZoomWatcher = ({ onZoomChange }) => {
  useMapEvents({
    zoomend: (event) => {
      const zoom = event.target.getZoom();
      onZoomChange(zoom);
    }
  });
  return null;
};

MapZoomWatcher.propTypes = {
  onZoomChange: PropTypes.func.isRequired
};

const ClientZipMap = ({
  clientId,
  parentCompanyId,
  clientIds = [],
  startDate,
  endDate,
  skipLeads = false,
}) => {
  const [mapData, setMapData] = useState({
    zips: [],
    territories: [],
    leads: [],
    meta: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapZoom, setMapZoom] = useState(LEAD_MARKER_STYLE.baselineZoom);
  const [isFullMapOpen, setIsFullMapOpen] = useState(false);
  const [selectedKpi, setSelectedKpi] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_KPI;
    try {
      const stored = window.localStorage.getItem(KPI_STORAGE_KEY);
      return KPI_OPTION_MAP[stored] ? stored : DEFAULT_KPI;
    } catch {
      return DEFAULT_KPI;
    }
  });

  const handleKpiChange = useCallback((event) => {
    const value = event.target.value;
    setSelectedKpi(value);
    try {
      window.localStorage.setItem(KPI_STORAGE_KEY, value);
    } catch {
      // Storage disabled / quota — ignore, in-memory selection still works.
    }
  }, []);

  const [onlyShowZipsWithData, setOnlyShowZipsWithData] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(KPI_FILTER_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const handleOnlyWithDataToggle = useCallback((event) => {
    const checked = event.target.checked;
    setOnlyShowZipsWithData(checked);
    try {
      window.localStorage.setItem(KPI_FILTER_STORAGE_KEY, checked ? 'true' : 'false');
    } catch {
      // Storage disabled / quota — ignore.
    }
  }, []);

  const selectedKpiOption = KPI_OPTION_MAP[selectedKpi] || KPI_OPTION_MAP[DEFAULT_KPI];

  useEffect(() => {
    const normalizedClientIds = Array.isArray(clientIds)
      ? clientIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id))
      : [];

    if (!clientId && !parentCompanyId && !normalizedClientIds.length) return;

    let isMounted = true;

    const params = {};
    if (startDate) params.date_after = formatDateToYMD(startDate);
    if (endDate) params.date_before = formatDateToYMD(endDate);
    if (clientId) params.client_id = clientId;
    if (parentCompanyId) params.parent_company_id = parentCompanyId;
    if (normalizedClientIds.length) params.client_ids = normalizedClientIds.join(',');
    if (skipLeads) params.skip_leads = true;

    // Coverage-only responses (skip_leads=true) are cacheable — boundaries
    // rarely change, the payload is ~1MB, and the approval flow pre-warms
    // the server cache. Keep one entry in sessionStorage per tab to make
    // tab-switch round-trips instant.
    const CACHE_KEY = 'client_zip_map_coverage';
    const CACHE_TTL_MS = 5 * 60 * 1000;
    const cacheSig = JSON.stringify(params);

    const readCache = () => {
      if (!skipLeads) return null;
      try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed.sig !== cacheSig) return null;
        if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
        return parsed.data;
      } catch {
        return null;
      }
    };

    const writeCache = (data) => {
      if (!skipLeads) return;
      try {
        sessionStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ sig: cacheSig, ts: Date.now(), data }),
        );
      } catch {
        // Quota / disabled — ignore.
      }
    };

    const applyData = (data) => {
      setMapData({
        zips: data?.zips || [],
        territories: data?.territories || [],
        leads: data?.leads || [],
        meta: data?.meta || {},
      });
    };

    const fetchData = async () => {
      const cached = readCache();
      if (cached) {
        applyData(cached);
        setLoading(false);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetchClientZipMap(params);
        if (!isMounted) return;
        const data = response.data;
        applyData(data);
        writeCache(data);
      } catch (err) {
        console.error('Failed to load client ZIP map data', err);
        if (isMounted) {
          setError('Unable to load map data right now.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [clientId, parentCompanyId, clientIds, startDate, endDate, skipLeads]);

  const territoryLookup = useMemo(() => {
    const lookup = {};
    mapData.territories.forEach((territory, index) => {
      const key = getTerritoryKey(territory.territory_id);
      lookup[key] = {
        ...territory,
        color: MAP_COLORS[index % MAP_COLORS.length]
      };
    });
    return lookup;
  }, [mapData.territories]);

  const resolveTerritoryColor = useCallback(
    (territoryId) => territoryLookup[getTerritoryKey(territoryId)]?.color || '#546e7a',
    [territoryLookup]
  );

  // Marker radius is driven by the selected KPI. Rates (0–1) get a different
  // scaling factor than raw counts/dollars so a 25% conversion rate doesn't
  // render as a single-pixel dot next to a 200-lead marker. By default,
  // zero-value ZIPs still render at minimum radius so the user can see all
  // their coverage; the "Only ZIPs with data" toggle filters them out.
  const leadZipMarkers = useMemo(() => {
    if (!Array.isArray(mapData.leads)) {
      return [];
    }

    return mapData.leads
      .map((bucket) => {
        const lat = Number(bucket.latitude);
        const lng = Number(bucket.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          return null;
        }
        const rawValue = bucket.kpis?.[selectedKpi];
        const numericValue = Number(rawValue);
        const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
        const hasValue = safeValue > 0;
        if (!hasValue && onlyShowZipsWithData) {
          return null;
        }
        const scaledValue =
          selectedKpiOption.format === 'percent' ? safeValue * 100 : safeValue;
        const baseRadius = hasValue
          ? LEAD_MARKER_STYLE.minRadius + Math.log(scaledValue + 1) * LEAD_MARKER_STYLE.scale
          : LEAD_MARKER_STYLE.minRadius;
        const zoomFactor =
          LEAD_MARKER_STYLE.zoomMultiplier ** (mapZoom - LEAD_MARKER_STYLE.baselineZoom);
        const scaledRadius = baseRadius * zoomFactor;
        const radius = Math.min(
          LEAD_MARKER_STYLE.maxRadius,
          Math.max(LEAD_MARKER_STYLE.minRadius, scaledRadius)
        );
        const color = bucket.has_out_of_area
          ? OUT_OF_AREA_COLOR
          : resolveTerritoryColor(bucket.client_territory_id);
        return {
          key: bucket.zip_code || `${lat}|${lng}`,
          lat,
          lng,
          radius: Number.isFinite(radius) ? radius : 6,
          color,
          // Fade zero-value dots so they read as "no activity" without
          // obscuring the real KPI signal nearby.
          fillOpacity: hasValue ? 0.85 : 0.25,
          bucket,
          selectedValue: rawValue,
        };
      })
      .filter(Boolean);
  }, [
    mapData.leads,
    mapZoom,
    resolveTerritoryColor,
    selectedKpi,
    selectedKpiOption,
    onlyShowZipsWithData,
  ]);

  // Single-client dashboards under the count cap ship per-ZIP simplified
  // geometry — render every ZIP as its own polygon. Parent dashboards and
  // very large single-client scopes get pre-dissolved territory polygons
  // instead (one per territory, one per status bucket).
  const usePerZipGeometry = mapData.meta?.geometry_mode === 'per_zip';

  const territoryFeatureCollection = useMemo(() => {
    if (usePerZipGeometry) {
      return { type: 'FeatureCollection', features: [] };
    }
    const features = [];
    mapData.territories.forEach((territory) => {
      const baseProps = {
        territoryId: territory.territory_id,
        territoryName: territory.territory_name || 'Unassigned',
        clientName: territory.client_name,
        zipCount: territory.zip_count,
      };
      const active = territory.dissolved_active_geojson;
      if (isPolygonGeometry(active)) {
        features.push({
          type: 'Feature',
          geometry: active,
          properties: { ...baseProps, isFiltered: false },
        });
      }
      const filtered = territory.dissolved_filtered_geojson;
      if (isPolygonGeometry(filtered)) {
        features.push({
          type: 'Feature',
          geometry: filtered,
          properties: { ...baseProps, isFiltered: true },
        });
      }
    });
    return { type: 'FeatureCollection', features };
  }, [mapData.territories, usePerZipGeometry]);

  const perZipFeatureCollection = useMemo(() => {
    if (!usePerZipGeometry) {
      return { type: 'FeatureCollection', features: [] };
    }
    const features = [];
    mapData.zips.forEach((zip) => {
      const geometry = zip.geometry;
      if (!isPolygonGeometry(geometry)) {
        return;
      }
      features.push({
        type: 'Feature',
        geometry,
        properties: {
          zipCode: zip.zip_code,
          territoryId: zip.territory_id,
          territoryName: zip.territory_name || 'Unassigned',
          clientName: zip.client_name,
          isFiltered: Boolean(zip.is_filtered_out),
        },
      });
    });
    return { type: 'FeatureCollection', features };
  }, [mapData.zips, usePerZipGeometry]);

  // Always render centroid dots — they double as hover targets for per-ZIP
  // tooltips even when territory polygons are present.
  const zipCentroidMarkers = useMemo(() => {
    return mapData.zips
      .map((zip) => {
        const lat = zip.centroid?.lat;
        const lng = zip.centroid?.lng;
        if (lat == null || lng == null) return null;
        return {
          key: zip.id,
          lat,
          lng,
          zipCode: zip.zip_code,
          territoryId: zip.territory_id,
          territoryName: zip.territory_name || 'Unassigned',
          clientName: zip.client_name,
          isFiltered: Boolean(zip.is_filtered_out),
        };
      })
      .filter(Boolean);
  }, [mapData.zips]);

  const territoryStyle = useCallback(
    (feature = {}) => {
      const props = feature.properties || {};
      const baseColor = resolveTerritoryColor(props.territoryId);
      const fillColor = props.isFiltered ? withAlpha(baseColor, 0.18) : withAlpha(baseColor, 0.45);
      return {
        color: baseColor,
        weight: 1.5,
        fillColor,
        fillOpacity: props.isFiltered ? 0.2 : 0.5,
      };
    },
    [resolveTerritoryColor]
  );

  const handleTerritoryEachFeature = useCallback((feature, layer) => {
    const props = feature?.properties || {};
    const tooltipHtml = `
      <div>
        <div><strong>Territory:</strong> ${props.territoryName || 'Unassigned'}</div>
        ${props.clientName ? `<div><strong>Client:</strong> ${props.clientName}</div>` : ''}
        <div><strong>ZIPs:</strong> ${props.zipCount ?? '—'}</div>
        <div><strong>Status:</strong> ${props.isFiltered ? 'Filtered' : 'Active'}</div>
      </div>
    `;
    layer.bindTooltip(tooltipHtml, { sticky: true });
  }, []);

  const perZipStyle = useCallback(
    (feature = {}) => {
      const props = feature.properties || {};
      const baseColor = resolveTerritoryColor(props.territoryId);
      const fillColor = props.isFiltered ? withAlpha(baseColor, 0.18) : withAlpha(baseColor, 0.45);
      return {
        color: baseColor,
        weight: 1,
        fillColor,
        fillOpacity: props.isFiltered ? 0.2 : 0.5,
      };
    },
    [resolveTerritoryColor]
  );

  const handlePerZipEachFeature = useCallback((feature, layer) => {
    const props = feature?.properties || {};
    const tooltipHtml = `
      <div>
        <div><strong>ZIP:</strong> ${props.zipCode || ''}</div>
        <div><strong>Territory:</strong> ${props.territoryName || 'Unassigned'}</div>
        ${props.clientName ? `<div><strong>Client:</strong> ${props.clientName}</div>` : ''}
        <div><strong>Status:</strong> ${props.isFiltered ? 'Filtered' : 'Active'}</div>
      </div>
    `;
    layer.bindTooltip(tooltipHtml, { sticky: true });
  }, []);

  const mapCenter = useMemo(() => {
    if (leadZipMarkers.length > 0) {
      return [leadZipMarkers[0].lat, leadZipMarkers[0].lng];
    }
    const zipWithCentroid = mapData.zips.find((zip) => zip.centroid);
    if (zipWithCentroid) {
      return [zipWithCentroid.centroid.lat, zipWithCentroid.centroid.lng];
    }
    return DEFAULT_CENTER;
  }, [leadZipMarkers, mapData.zips]);

  const mapCenterIsDefault =
    leadZipMarkers.length === 0 && !mapData.zips.some((zip) => zip.centroid);

  const renderLegend = () => {
    if (!mapData.territories.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No ZIP boundaries are available for this scope yet.
        </Typography>
      );
    }

    return (
      <Stack direction="row" flexWrap="wrap" spacing={2} rowGap={1}>
        {mapData.territories.map((territory) => {
          const color = resolveTerritoryColor(territory.territory_id);
          return (
            <Stack key={`${territory.territory_id || 'none'}`} direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '2px',
                  backgroundColor: color,
                  border: '1px solid rgba(0,0,0,0.2)'
                }}
              />
              <Typography variant="body2">
                {(territory.client_name ? `${territory.client_name} - ` : '') + territory.territory_name} ({territory.zip_count})
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    );
  };

  const mapTitle = parentCompanyId && !clientId
    ? 'Parent Company Territory Map'
    : 'Client Territory Map';
  const mapSubtitle = parentCompanyId && !clientId
    ? 'ZIP shapes are shaded by territory across all selected clients. Filtered ZIPs appear lighter, and red dots highlight ZIPs with out-of-area leads.'
    : 'ZIP shapes are shaded by territory. Filtered ZIPs appear lighter, and red dots highlight ZIPs with out-of-area leads.';

  const renderMap = (height, boxProps = {}) => (
    <Box sx={{ height, ...boxProps }}>
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        minZoom={3}
        maxZoom={14}
        maxBounds={NORTH_AMERICA_BOUNDS}
        maxBoundsViscosity={1}
        scrollWheelZoom
        preferCanvas
        style={{ height: '100%', width: '100%' }}
      >
        <MapAutoCenter
          key={`autocenter-${clientId || ''}-${parentCompanyId || ''}-${(clientIds || []).join(',')}`}
          center={mapCenter}
          isDefault={mapCenterIsDefault}
        />
        <MapZoomWatcher onZoomChange={setMapZoom} />
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          subdomains={['a', 'b', 'c', 'd']}
          maxZoom={19}
          keepBuffer={1}
          updateWhenIdle
          updateWhenZooming={false}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
        {territoryFeatureCollection.features.length > 0 && (
          <GeoJSON
            key={`territories-${territoryFeatureCollection.features.length}`}
            data={territoryFeatureCollection}
            style={territoryStyle}
            onEachFeature={handleTerritoryEachFeature}
          />
        )}
        {perZipFeatureCollection.features.length > 0 && (
          <GeoJSON
            key={`per-zip-${perZipFeatureCollection.features.length}`}
            data={perZipFeatureCollection}
            style={perZipStyle}
            onEachFeature={handlePerZipEachFeature}
          />
        )}
        {zipCentroidMarkers.map((point) => {
          const baseColor = resolveTerritoryColor(point.territoryId);
          return (
            <CircleMarker
              key={`czm-${point.key}`}
              center={[point.lat, point.lng]}
              radius={3}
              pathOptions={{
                color: baseColor,
                weight: 0.5,
                fillColor: baseColor,
                fillOpacity: point.isFiltered ? 0.25 : 0.65,
              }}
            >
              <Tooltip direction="top" offset={[0, -4]}>
                <span>
                  ZIP {point.zipCode} · {point.territoryName}
                </span>
              </Tooltip>
            </CircleMarker>
          );
        })}
        {leadZipMarkers.map((point) => (
          <CircleMarker
            key={point.key}
            center={[point.lat, point.lng]}
            radius={point.radius}
            pathOptions={{
              color: 'transparent',
              weight: 0,
              fillColor: point.color,
              fillOpacity: point.fillOpacity ?? 0.85,
            }}
          >
            <Tooltip direction="top" offset={[0, -point.radius]}>
              <Box sx={{ minWidth: 220 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  ZIP {point.bucket.zip_code || 'Unknown'}
                </Typography>
                <Typography variant="body2">
                  Client: {point.bucket.client_name || 'Unknown'}
                </Typography>
                <Typography variant="body2">
                  Territory:{' '}
                  {point.bucket.has_out_of_area ? 'Out Of Area' : point.bucket.client_territory_name || 'Unassigned'}
                </Typography>
                <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 1.5, rowGap: 0.25 }}>
                  {KPI_OPTIONS.map((option) => {
                    const value = point.bucket.kpis?.[option.key];
                    const isSelected = option.key === selectedKpi;
                    return (
                      <Box key={option.key} sx={{ display: 'contents' }}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: isSelected ? 700 : 400 }}
                        >
                          {option.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: isSelected ? 700 : 400, textAlign: 'right' }}
                        >
                          {formatKpiValue(value, option.format)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </Box>
  );

  return (
    <Card>
      <CardHeader
        title={mapTitle}
        subheader={mapSubtitle}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="zip-map-kpi-label">Color by</InputLabel>
              <Select
                labelId="zip-map-kpi-label"
                label="Color by"
                value={selectedKpi}
                onChange={handleKpiChange}
              >
                {KPI_OPTIONS.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={onlyShowZipsWithData}
                  onChange={handleOnlyWithDataToggle}
                />
              )}
              label={(
                <Typography variant="caption">Only ZIPs with data</Typography>
              )}
              sx={{ mr: 0 }}
            />
            <Button size="small" onClick={() => setIsFullMapOpen(true)} disabled={loading || !!error}>
              View Full Map
            </Button>
          </Stack>
        }
      />
      <CardContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {renderLegend()}
            {mapData.territories.length > 0 || leadZipMarkers.length > 0 ? (
              renderMap(MAP_HEIGHT, { mt: 2 })
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No qualifying ZIPs or leads were found for this selection.
              </Typography>
            )}
          </>
        )}
      </CardContent>
      <Dialog fullScreen open={isFullMapOpen} onClose={() => setIsFullMapOpen(false)}>
        <DialogTitle>{mapTitle}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>{renderMap(FULLSCREEN_MAP_HEIGHT)}</DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFullMapOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

ClientZipMap.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clientIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  skipLeads: PropTypes.bool,
};

export default ClientZipMap;
